// 开发模式使用本地接口，生产模式使用线上接口
import { WORKER_API } from '../config.js';

export const fetchDefiProtocols = async (page = 1, search = '', category = '', blockchain = '', token = '', sortBy = 'tvl', signal = null) => {
  try {
    // 首页使用带 APY pools 的协议列表接口
    const url = new URL('/api/protocols/withapy', WORKER_API);
    url.searchParams.set('page', page);
    if (search) {
      url.searchParams.set('search', search);
    }
    if (category) {
      url.searchParams.set('category', category);
    }
    if (blockchain) {
      url.searchParams.set('blockchain', blockchain);
    }
    if (token) {
      url.searchParams.set('token', token);
    }
    if (sortBy) {
      url.searchParams.set('sortBy', sortBy);
    }
    
    const response = await fetch(url, { signal });
    if (!response.ok) {
      throw new Error('Failed to fetch data');
    }
    return await response.json();
  } catch (error) {
    // 如果是取消请求，直接抛出，让调用者处理
    if (error.name === 'AbortError') {
      throw error;
    }
    console.error('Error fetching DeFi protocols:', error);
    return { 
      protocols: [], 
      pagination: { currentPage: 1, totalPages: 1, totalItems: 0, hasMore: false }, 
      filters: { 
        categories: { popular: [], others: [] }, 
        blockchains: { popular: [], others: [] } 
      } 
    };
  }
};

export const fetchProtocolScore = async (protocolId) => {
  if (!protocolId) {
    throw new Error('Missing protocol id');
  }

  try {
    const url = new URL('/api/getScore', WORKER_API);
    url.searchParams.set('id', protocolId);

    const response = await fetch(url);
    if (!response.ok) {
      throw new Error('Failed to fetch protocol score');
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching protocol score:', error);
    throw error;
  }
};

export const fetchProtocolPools = async (slug) => {
  if (!slug) {
    throw new Error('Missing protocol slug');
  }

  try {
    const url = new URL(`/api/pools/${encodeURIComponent(slug)}`, WORKER_API);
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error('Failed to fetch protocol pools');
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching protocol pools:', error);
    throw error;
  }
};

export const fetchTokens = async (search = '', limit = 1000, sortBy = 'symbol') => {
  try {
    const url = new URL('/api/tokens', WORKER_API);
    if (search) {
      url.searchParams.set('search', search);
    }
    if (limit) {
      url.searchParams.set('limit', limit);
    }
    if (sortBy) {
      url.searchParams.set('sortBy', sortBy);
    }
    
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error('Failed to fetch tokens');
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching tokens:', error);
    throw error;
  }
};

// 一次性获取首页需要的选项（分类 / 链 / token）- 初始页
export const fetchOptions = async (page = 1, pageSize = 10) => {
  try {
    const url = new URL('/api/options', WORKER_API);
    url.searchParams.set('page', page);
    url.searchParams.set('pageSize', pageSize);
    const response = await fetch(url.toString());
    if (!response.ok) {
      throw new Error('Failed to fetch options');
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching options:', error);
    // 兜底返回空结构，避免前端直接崩
    return {
      success: false,
      categories: [],
      blockchains: {
        popular: [],
        others: [],
        all: [],
      },
      tokens: [],
    };
  }
};

// 分页获取分类选项
export const fetchCategoryOptions = async (page = 1, pageSize = 10) => {
  const url = new URL('/api/options/categories', WORKER_API);
  url.searchParams.set('page', page);
  url.searchParams.set('pageSize', pageSize);

  const res = await fetch(url.toString());
  if (!res.ok) throw new Error('Failed to fetch category options');
  return res.json();
};

// 分页获取链选项
export const fetchBlockchainOptions = async (page = 1, pageSize = 10) => {
  const url = new URL('/api/options/blockchains', WORKER_API);
  url.searchParams.set('page', page);
  url.searchParams.set('pageSize', pageSize);

  const res = await fetch(url.toString());
  if (!res.ok) throw new Error('Failed to fetch blockchain options');
  return res.json();
};

// 分页获取 token 选项（仅用于无搜索时的热门 token）
export const fetchTokenOptions = async (page = 1, pageSize = 10) => {
  const url = new URL('/api/options/tokens', WORKER_API);
  url.searchParams.set('page', page);
  url.searchParams.set('pageSize', pageSize);

  const res = await fetch(url.toString());
  if (!res.ok) throw new Error('Failed to fetch token options');
  return res.json();
};

// 获取所有 pools（用于收益比较页面）
export const fetchAllPools = async ({ token = null, chain = null, protocolId = null, minScore = null, sortBy = 'apy', page = 1, pageSize = 100 } = {}) => {
  try {
    const url = new URL('/api/pools/all', WORKER_API);
    if (token) {
      url.searchParams.set('token', token);
    }
    if (chain) {
      url.searchParams.set('chain', chain);
    }
    if (protocolId) {
      url.searchParams.set('protocolId', protocolId);
    }
    if (minScore !== null && minScore !== undefined) {
      url.searchParams.set('minScore', minScore);
    }
    if (sortBy) {
      url.searchParams.set('sortBy', sortBy);
    }
    url.searchParams.set('page', page);
    url.searchParams.set('pageSize', pageSize);
    
    const response = await fetch(url.toString());
    if (!response.ok) {
      throw new Error('Failed to fetch all pools');
    }
    return await response.json();
  } catch (error) {
    console.error('Error fetching all pools:', error);
    throw error;
  }
};
